import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const LatePaymentsReport = ({
  payments = [],
  filterInfo = {
    daysOverdueRange: { preset: "all", min: "", max: "" },
  },
  companyInfo = {
    name: "Your Company Name",
    address: "123, Avenue Mohammed V, Casablanca",
    phone: "+212 5XX-XXXXXX",
    email: "contact@company.com",
  },
}) => {
  const [isPrinting, setIsPrinting] = useState(false);

  const today = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Create unique payments to avoid double counting
  const uniquePayments = payments.reduce((acc, payment) => {
    const key = `${payment.numero_vente}_${payment.numero_paiement}`;
    if (!acc[key]) {
      acc[key] = payment;
    }
    return acc;
  }, {});

  const totalAmount = Object.values(uniquePayments).reduce(
    (sum, payment) => sum + parseFloat(payment.montant || 0),
    0
  );

  const totalRemainingAmount = Object.values(uniquePayments).reduce(
    (sum, payment) => sum + parseFloat(payment.solde_restant || 0),
    0
  );

  const getRangeLabel = () => {
    switch (filterInfo.daysOverdueRange.preset) {
      case "all":
        return "Tous les retards";
      case "30":
        return "1-30 jours de retard";
      case "60":
        return "31-60 jours de retard";
      case "90":
        return "61-90 jours de retard";
      case "90+":
        return "Plus de 90 jours de retard";
      case "custom":
        return `${filterInfo.daysOverdueRange.min || 0}-${
          filterInfo.daysOverdueRange.max || "∞"
        } jours de retard`;
      default:
        return "Période personnalisée";
    }
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        throw new Error(
          "Unable to open print window. Please check if pop-ups are blocked."
        );
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Rapport des Paiements en Retard</title>
            <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap">
            <style>
              @page {
                size: A4;
                margin: 10mm;
              }
              
              * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
              }

              body {
                font-family: 'Open Sans', Arial, sans-serif;
                line-height: 1.4;
                color: #2c3e50;
                background: #fff;
                font-size: 10px;
              }

              .page {
                position: relative;
                width: 210mm;
                min-height: 297mm;
                padding: 10mm;
                margin: 0 auto;
                background: white;
              }

              .company-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 20px;
              }

              .company-info {
                flex: 1;
              }

              .company-logo {
                background: #1a237e;
                width: 48px;
                height: 48px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: bold;
                margin-bottom: 8px;
                border-radius: 4px;
              }

              .company-details {
                font-size: 9px;
                color: #546e7a;
                line-height: 1.4;
              }

              .report-title {
                color: #d32f2f;
                font-size: 18px;
                font-weight: 700;
                margin: 15px 0;
                text-align: center;
                letter-spacing: 0.5px;
              }

              .report-subtitle {
                color: #37474f;
                font-size: 12px;
                font-weight: 600;
                margin: -10px 0 15px;
                text-align: center;
              }

              .report-metadata {
                text-align: right;
                margin-bottom: 15px;
                font-size: 9px;
                color: #546e7a;
              }

              .report-metadata p {
                margin: 0;
                line-height: 1.5;
              }

              .section-title {
                font-size: 11px;
                font-weight: 600;
                color: #37474f;
                margin: 15px 0 8px;
                text-transform: uppercase;
              }

              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 15px;
                table-layout: fixed;
              }

              th {
                background-color: #f5f5f5;
                color: #37474f;
                font-weight: 600;
                text-align: left;
                padding: 6px;
                border: 1px solid #e0e0e0;
                font-size: 9px;
              }

              td {
                padding: 4px 6px;
                border: 1px solid #e0e0e0;
                color: #455a64;
                font-size: 9px;
                word-wrap: break-word;
                vertical-align: top;
                line-height: 1.3;
              }

              .amount-cell {
                text-align: right;
                font-family: 'Courier New', monospace;
              }

              .days-late-cell {
                text-align: center;
                color: #d32f2f;
                font-weight: 600;
              }

              .reference-cell {
                font-family: 'Courier New', monospace;
                color: #0277bd;
              }

              .summary-table td {
                padding: 12px;
              }

              .summary-table td:first-child {
                width: 70%;
              }

              .total-row {
                font-weight: 600;
                background-color: #fafafa;
              }

              .total-row td {
                border-top: 2px solid #37474f;
              }

              .signature-section {
                display: flex;
                justify-content: space-between;
                margin-top: 20px;
                margin-bottom: 30px;
              }

              .signature-box {
                width: 45%;
              }

              .signature-line {
                border-bottom: 1px solid #90a4ae;
                margin-bottom: 4px;
                padding-top: 20px;
              }

              .signature-title {
                text-align: center;
                color: #546e7a;
                font-size: 9px;
                font-weight: 600;
              }

              .footer {
                position: absolute;
                bottom: 10mm;
                left: 10mm;
                right: 10mm;
                text-align: center;
                border-top: 1px solid #e0e0e0;
                padding-top: 8px;
              }

              .footer-text {
                color: #78909c;
                font-size: 8px;
                line-height: 1.4;
              }

              @media print {
                body {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }

                .page {
                  margin: 0;
                  padding: 20mm;
                }

                .company-logo {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="page">
              <div class="company-header">
                <div class="company-info">
                  <div class="company-logo">DEV</div>
                  <div class="company-details">
                    ${companyInfo.name}<br>
                    ${companyInfo.address}<br>
                    Tél: ${companyInfo.phone}<br>
                    Email: ${companyInfo.email}
                  </div>
                </div>
              </div>

              <div class="report-title">
                RAPPORT DES PAIEMENTS EN RETARD
              </div>
              <div class="report-subtitle">
                ${getRangeLabel()}
              </div>
              <div class="report-metadata">
                <p>Date du rapport: ${today}</p>
                <p>Réf: RPR-${new Date().toISOString().split("T")[0]}</p>
              </div>

              <div class="section-title">RÉCAPITULATIF</div>
              <table class="summary-table">
                <tr>
                  <td>Nombre total de paiements en retard</td>
                  <td class="amount-cell">${payments.length}</td>
                </tr>
                <tr>
                  <td>Montant total dû</td>
                  <td class="amount-cell">${totalAmount.toLocaleString(
                    "fr-FR",
                    {
                      style: "currency",
                      currency: "MAD",
                      minimumFractionDigits: 2,
                    }
                  )}</td>
                </tr>
                <tr>
                  <td>Montant total restant à payer</td>
                  <td class="amount-cell">${totalRemainingAmount.toLocaleString(
                    "fr-FR",
                    {
                      style: "currency",
                      currency: "MAD",
                      minimumFractionDigits: 2,
                    }
                  )}</td>
                </tr>
              </table>

              <div class="section-title">DÉTAIL DES PAIEMENTS EN RETARD</div>
              <table>
                <thead>
                  <tr>
                    <th style="width: 20%">Client</th>
                    <th style="width: 15%">Téléphone</th>
                    <th style="width: 12%">N° Vente</th>
                    <th style="width: 13%">N° Paiement</th>
                    <th style="width: 15%">Date Échéance</th>
                    <th style="width: 10%">Jours de Retard</th>
                    <th style="width: 15%">Montant</th>
                    <th style="width: 15%">Reste à Payer</th>
                  </tr>
                </thead>
                <tbody>
                  ${payments
                    .map(
                      (payment) => `
                    <tr>
                      <td>${payment.nom} ${payment.prenom}</td>
                      <td>${payment.telephone || "-"}</td>
                      <td class="reference-cell">${payment.numero_vente}</td>
                      <td class="reference-cell">${payment.numero_paiement}</td>
                      <td>${new Date(payment.date_echeance).toLocaleDateString(
                        "fr-FR"
                      )}</td>
                      <td class="days-late-cell">${payment.jours_retard}</td>
                      <td class="amount-cell">${parseFloat(
                        payment.montant
                      ).toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                      })}</td>
                      <td class="amount-cell">${parseFloat(
                        payment.solde_restant
                      ).toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                      })}</td>
                    </tr>
                  `
                    )
                    .join("")}
                  <tr class="total-row">
                    <td colspan="6">Total</td>
                    <td class="amount-cell">${totalAmount.toLocaleString(
                      "fr-FR",
                      {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                      }
                    )}</td>
                    <td class="amount-cell">${totalRemainingAmount.toLocaleString(
                      "fr-FR",
                      {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                      }
                    )}</td>
                  </tr>
                </tbody>
              </table>

              <div class="signature-section">
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-title">Préparé par</div>
                </div>
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-title">Approuvé par</div>
                </div>
              </div>

              <div class="footer">
                <div class="footer-text">
                  Ce rapport est généré automatiquement et ne nécessite pas de signature électronique<br>
                  ${companyInfo.name} • ${companyInfo.address} • Tél: ${
                    companyInfo.phone
                  }
                </div>
              </div>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setIsPrinting(false);
      }, 250);
    } catch (error) {
      console.error("Print error:", error);
      alert("Error generating PDF: " + error.message);
      setIsPrinting(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="error"
        startIcon={
          isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />
        }
        onClick={handlePrint}
        disabled={isPrinting}
      >
        {isPrinting ? "Génération du PDF..." : "Télécharger PDF"}
      </Button>
    </Box>
  );
};

export default LatePaymentsReport;
